import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AuditTrail from './AuditTrail';
import { CustomTabPanel, a11yProps } from '../../customTabs/CustomTabPanel';
import FolderNodes from '../assembliesAndItems/folderManager/FolderNodes';
import { useFetchAssemblyFolders } from '../../hooks/useFetchFolders';
import { GetFoldersResponse } from '../../api/protosCompiled/folder/folder_pb';

import { useStoreWithEqualityFn } from 'zustand/traditional';
import Breakouts from './Breakouts';
import BreakoutSelector from './BreakoutSelector';
import { Paper, Box, useTheme, alpha, Dialog, DialogContent } from '@mui/material';
import { OptimizedFilterBar } from './OptimizedFilterBar';
import { QuickAssembly } from './QuickAssembly';
import { useUnityBuildStore } from '../../stores/store';
import { shallow } from 'zustand/shallow';
import {
  useFetchChangeOrderBreakouts,
  useFetchEstimateBreakouts,
} from '../../hooks/useFetchBreakout';
import { useEffect, useState, useRef } from 'react';
import { getContrastTextColor } from '../../utils/getCintrastTextColor';
import { HelpBubble } from '../common/helpBubbles';
import React from 'react';
import { AiCountManager } from './AiCountManager';
import { Z_INDEX_LAYERS } from '../../utils/zIndexLayers';

function TakeoffPallette() {
  const theme = useTheme();

  const { selectedEstimate, selectedChangeOrder, selectedBreakout, setSelectedBreakout } =
    useStoreWithEqualityFn(
      useUnityBuildStore,
      state => ({
        selectedEstimate: state.selectedEstimate,
        selectedChangeOrder: state.selectedChangeOrder,
        selectedBreakout: state.selectedBreakout,
        setSelectedBreakout: state.setSelectedBreakout,
      }),
      shallow
    );

  const [value, setValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickAssembly, setShowQuickAssembly] = useState(false);

  // Global filtering state for takeoff list
  const {
    selectedFilter,
    setSelectedFilter,
    breakoutFilterObject,
    setBreakoutFilterObject,
    selectedObject,
    currentPage,
  } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      selectedFilter: state.selectedFilter,
      setSelectedFilter: state.setSelectedFilter,
      breakoutFilterObject: state.breakoutFilterObject,
      setBreakoutFilterObject: state.setBreakoutFilterObject,
      selectedObject: state.selectedObject,
      currentPage: state.currentPage,
    }),
    shallow
  );
  const breakoutsTabRef = useRef<HTMLDivElement>(null);
  const [showHelpBubble, setShowHelpBubble] = useState(false);

  const { data: estimateBreakouts, isLoading: isLoadingEstimateBreakouts } =
    useFetchEstimateBreakouts(selectedEstimate?.estimateid || '');
  const { data: changeOrderBreakouts, isLoading: isLoadingChangeOrderBreakouts } =
    useFetchChangeOrderBreakouts(selectedChangeOrder?.changeorderid || '');

  const breakout = estimateBreakouts ? estimateBreakouts : changeOrderBreakouts;
  const isLoading = isLoadingEstimateBreakouts || isLoadingChangeOrderBreakouts;

  useEffect(() => {
    if (!selectedBreakout && breakout && !isLoading) {
      setSelectedBreakout(breakout.breakoutsList[0]);
    }
  }, [isLoading, selectedBreakout, breakout, setSelectedBreakout]);

  // Show help bubble if no breakout is selected
  useEffect(() => {
    if (!selectedBreakout && !isLoading) {
      setShowHelpBubble(true);
    } else {
      setShowHelpBubble(false);
    }
  }, [selectedBreakout, isLoading]);

  const { data: folderData } = useFetchAssemblyFolders();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Determine background color based on theme and user preference
  const backgroundColor =
    theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : theme.palette.background.default;

  // Get text color based on background
  const textColor = getContrastTextColor(backgroundColor);

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'backgroundColor',
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: 'none',
        zIndex: Z_INDEX_LAYERS.SYSTEM.TAKEOFF_PALETTE,
        position: 'relative', // Add this for help bubble positioning
      }}
    >
      {/* Header: Breakout selector + inline filter bar + Quick Assembly handled here */}
      <Box
        sx={{
          borderBottom: `1px solid ${alpha(textColor, 0.1)}`,
          py: 0.5,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <BreakoutSelector size="small" />
        <OptimizedFilterBar
          variant="inline"
          searchTerm={searchTerm}
          onSearchChange={e => setSearchTerm(e.target.value)}
          onClearSearch={() => setSearchTerm('')}
          selectedFilter={selectedFilter}
          onFilterChange={filter => setSelectedFilter(filter)}
          breakoutFilterObject={breakoutFilterObject}
          onBreakoutFilterChange={event => {
            const filter = event.target.value as string;
            const selected =
              filter === 'all' ? null : (breakout?.breakoutsList || []).find(b => b.id === filter);
            setBreakoutFilterObject(selected || null);
          }}
          currentBreakouts={breakout?.breakoutsList || []}
          onQuickAssemblyClick={() => setShowQuickAssembly(true)}
          showQuickAssemblyButton
          onClearAllFilters={() => {
            setSearchTerm('');
            setSelectedFilter('all');
            setBreakoutFilterObject(null);
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        scrollButtons="auto"
        aria-label="takeoff-tabs"
        sx={{
          borderBottom: 1,
          borderColor: alpha(textColor, 0.1),
          minHeight: 48,
          '& .MuiTab-root': {
            color: alpha(textColor, 0.7),
            minHeight: 48,
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            '&.Mui-selected': {
              color: textColor,
              fontWeight: 600,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
            height: 3,
          },
        }}
      >
        <Tab label="Audit Trail" {...a11yProps(0)} />
        <Tab label="Assemblies" {...a11yProps(1)} />
        <Tab label="Breakouts" {...a11yProps(2)} ref={breakoutsTabRef} />
        {/*Activate for development only*/}
        {process.env.NODE_ENV === 'development' && <Tab label="AI Count" {...a11yProps(3)} />}
      </Tabs>

      {/* Help Bubble */}
      {showHelpBubble && breakoutsTabRef.current && (
        <HelpBubble
          message="Please create a breakout to begin"
          coordinates={{
            x: breakoutsTabRef.current.offsetLeft + breakoutsTabRef.current.offsetWidth / 2 - 150,
            y: breakoutsTabRef.current.offsetTop + breakoutsTabRef.current.offsetHeight + 85, // Position below the tabb
          }}
        />
      )}

      {/* Tab Panels container */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.7)
              : alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <CustomTabPanel value={value} index={0}>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              width: '100%',
              maxHeight: '100%',
            }}
          >
            <AuditTrail searchTerm={searchTerm} />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              width: '100%',
              maxHeight: '100%',
            }}
          >
            <FolderNodes
              context="secondary"
              folderType="assembly"
              folderData={folderData as GetFoldersResponse.AsObject}
            />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              width: '100%',
              maxHeight: '100%',
            }}
          >
            <Breakouts />
          </Box>
        </CustomTabPanel>
        {
          <CustomTabPanel value={value} index={3}>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                width: '100%',
                maxHeight: '100%',
              }}
            >
              <AiCountManager />
            </Box>
          </CustomTabPanel>
        }
      </Box>

      {/* Quick Assembly Dialog moved here to keep header compact */}
      <Dialog
        open={showQuickAssembly}
        onClose={() => setShowQuickAssembly(false)}
        fullScreen={false}
      >
        <DialogContent>
          <QuickAssembly
            open={showQuickAssembly}
            onClose={() => setShowQuickAssembly(false)}
            selectedObject={selectedObject}
            currentPage={currentPage}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

export default React.memo(TakeoffPallette);
